(function () {
  function parseJsonScript(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || "{}");
    } catch (e) {
      return null;
    }
  }

  function uniq(arr) {
    return Array.from(new Set(arr.filter(Boolean)));
  }

  function formatMoney(cents) {
    // Very small formatter; avoids needing theme helpers.
    const value = (Number(cents) || 0) / 100;
    return new Intl.NumberFormat(undefined, { style: "currency", currency: "GBP" }).format(value);
  }

  function metersToFeet(m) {
    return (Number(m) || 0) * 3.280839895;
  }

  function feetToMeters(ft) {
    return (Number(ft) || 0) / 3.280839895;
  }

  function clampInt(v, min, max) {
    const n = Math.round(Number(v) || 0);
    return Math.max(min, Math.min(max, n));
  }

  function findVariantForHeight(product, height) {
    if (!product || !product.variants) return null;
    const target = normalizeHeight(height);
    return product.variants.find((v) => v.available && normalizeHeight(getVariantHeight(v)) === target) ||
      product.variants.find((v) => normalizeHeight(getVariantHeight(v)) === target) ||
      null;
  }

  function normalizeHeight(val) {
    if (!val) return "";
    const s = String(val).trim().toLowerCase();
    // normalize like "4ft", "4 ft", "4FT" -> "4ft"
    const m = s.match(/(\d+(?:\.\d+)?)\s*ft/);
    if (m) return `${m[1]}ft`;
    const n = s.match(/^\d+(?:\.\d+)?$/);
    if (n) return `${s}ft`;
    return s;
  }

  function heightToNumberFt(val) {
    const s = normalizeHeight(val);
    const m = s.match(/(\d+(?:\.\d+)?)ft/);
    if (!m) return null;
    const n = Number(m[1]);
    return Number.isFinite(n) ? n : null;
  }

  function findBestVariantAtOrAboveHeight(product, height) {
    if (!product || !product.variants) return null;
    const targetFt = heightToNumberFt(height);
    if (targetFt == null) return findVariantForHeight(product, height);

    const candidates = product.variants
      .map((v) => ({ v, ft: heightToNumberFt(getVariantHeight(v)) }))
      .filter((x) => x.ft != null)
      .sort((a, b) => a.ft - b.ft);

    // Prefer available variants
    const available = candidates.filter((x) => x.v.available);
    const list = available.length ? available : candidates;

    const firstAtOrAbove = list.find((x) => x.ft >= targetFt);
    if (firstAtOrAbove) return firstAtOrAbove.v;

    // If none at/above, fallback to exact match or first candidate
    return findVariantForHeight(product, height) || (list[0] ? list[0].v : null);
  }

  function getVariantHeight(variant) {
    if (!variant) return "";
    // Prefer explicit height field if present
    if (variant.height) return variant.height;

    // Otherwise try to extract from any option string, examples:
    // - "6ft x 4ft High" -> 4ft (last ft match)
    // - "2ft" -> 2ft
    // - "6 x 4" -> 4ft (last number)
    const opts = Array.isArray(variant.options) ? variant.options : [];
    const title = variant.title ? String(variant.title) : "";
    const joined = (opts.join(" ") + " " + title).toLowerCase();

    const ftMatches = joined.matchAll(/(\d+(?:\.\d+)?)\s*ft/g);
    let lastFt = null;
    for (const m of ftMatches) lastFt = m && m[1];
    if (lastFt) return `${lastFt}ft`;

    // Try patterns like "6 x 4" or "6×4"
    const xMatch = joined.match(/(\d+(?:\.\d+)?)\s*(?:x|×)\s*(\d+(?:\.\d+)?)/);
    if (xMatch) return `${xMatch[2]}ft`;

    // Fallback: last standalone number becomes ft
    const nums = joined.match(/(\d+(?:\.\d+)?)/g);
    if (nums && nums.length) return `${nums[nums.length - 1]}ft`;

    return "";
  }

  function buildChoiceCard({ group, id, title, image, selected }) {
    const div = document.createElement("div");
    div.className = "city-fence-calc__choice" + (selected ? " is-selected" : "");
    div.setAttribute("role", "button");
    div.tabIndex = 0;
    div.dataset.choiceGroup = group;
    div.dataset.choiceId = String(id);
    div.innerHTML = `
      ${image ? `<img src="${image}" alt="">` : ``}
      <div class="city-fence-calc__choice-body">
        <div class="city-fence-calc__choice-title">${title || ""}</div>
      </div>
    `;
    return div;
  }

  function initCalculator(root) {
    const config = parseJsonScript(root.closest("[data-section-type='city-fence-calculator']").querySelector("[data-calc-config]")) || {};
    const productsData = parseJsonScript(root.closest("[data-section-type='city-fence-calculator']").querySelector("[data-calc-products]")) || {};

    const panels = Array.isArray(productsData.panels) ? productsData.panels : [];
    const posts = Array.isArray(productsData.posts) ? productsData.posts : [];
    const gravelboards = Array.isArray(productsData.gravelboards) ? productsData.gravelboards : [];

    const elLengthFt = root.querySelector("[data-length-ft]");
    const elLengthM = root.querySelector("[data-length-m]");
    const elHeight = root.querySelector("[data-height]");

    const panelsChoices = root.querySelector('[data-choices="panels"]');
    const postsChoices = root.querySelector('[data-choices="posts"]');
    const gravelChoices = root.querySelector('[data-choices="gravel"]');
    const resultsEl = root.querySelector("[data-results]");
    const summaryEl = root.querySelector("[data-results-summary]");

    const steps = Array.from(root.querySelectorAll("[data-step]"));
    const indicators = Array.from(root.querySelectorAll("[data-step-indicator]"));

    const state = {
      step: 1,
      lengthFt: 0,
      height: "",
      panelProductId: null,
      postProductId: null,
      gravelProductId: null, // null means none
      panelWidthFt: Number(config.panelWidthFt || 6),
      results: null
    };

    const heights = uniq(
      panels.flatMap((p) => (p.variants || []).map((v) => normalizeHeight(getVariantHeight(v)))).filter(Boolean)
    );

    const fallbackHeights = ["2ft", "3ft", "4ft", "5ft", "6ft", "7ft"];
    const sortedHeights = (heights.length ? heights : fallbackHeights).sort((a, b) => {
      // Try to sort like 3ft, 4ft, 6ft by numeric prefix
      const an = parseFloat(String(a));
      const bn = parseFloat(String(b));
      if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
      return String(a).localeCompare(String(b));
    });

    elHeight.innerHTML = `<option value="">Select height</option>` + sortedHeights.map((h) => `<option value="${String(h)}">${String(h)}</option>`).join("");

    function setStep(nextStep) {
      state.step = nextStep;
      steps.forEach((p) => p.classList.toggle("is-active", Number(p.dataset.step) === nextStep));
      indicators.forEach((i) => i.classList.toggle("is-active", Number(i.dataset.stepIndicator) === Math.min(nextStep, 4)));
    }

    function validateStep1() {
      if (!state.lengthFt || state.lengthFt <= 0) return false;
      if (!state.height) return false;
      return true;
    }

    function renderChoices(group, container, items, selectedId) {
      container.innerHTML = "";
      if (group === "gravel") {
        const noneCard = buildChoiceCard({ group, id: "none", title: "No gravel board", image: "", selected: selectedId === null });
        container.appendChild(noneCard);
      }
      items.forEach((p) => {
        const card = buildChoiceCard({
          group,
          id: p.id,
          title: p.title,
          image: p.image,
          selected: String(p.id) === String(selectedId)
        });
        container.appendChild(card);
      });
    }

    function filterByHeight(list, mode) {
      if (mode === "atOrAbove") {
        return list.filter((p) => !!findBestVariantAtOrAboveHeight(p, state.height));
      }
      return list.filter((p) => !!findVariantForHeight(p, state.height));
    }

    function recalcResults() {
      const panel = panels.find((p) => String(p.id) === String(state.panelProductId));
      const post = posts.find((p) => String(p.id) === String(state.postProductId));
      const gravel = state.gravelProductId ? gravelboards.find((p) => String(p.id) === String(state.gravelProductId)) : null;

      const panelVariant = findVariantForHeight(panel, state.height);
      // Posts are often taller than the fence height; pick closest suitable post variant
      const postVariant = findBestVariantAtOrAboveHeight(post, state.height);
      const gravelVariant = gravel ? findVariantForHeight(gravel, state.height) : null;

      const panelQty = Math.ceil(state.lengthFt / state.panelWidthFt);
      const postQty = panelQty + 1;
      const gravelQty = gravel ? panelQty : 0;

      const items = [];
      if (panel && panelVariant) items.push({ kind: "panel", product: panel, variant: panelVariant, qty: panelQty });
      if (post && postVariant) items.push({ kind: "post", product: post, variant: postVariant, qty: postQty });
      if (gravel && gravelVariant) items.push({ kind: "gravel", product: gravel, variant: gravelVariant, qty: gravelQty });

      state.results = { items };
    }

    function renderResults() {
      if (!state.results) return;
      const { items } = state.results;

      const head = `
        <div class="city-fence-calc__results-head">
          <div>Item</div><div></div><div>Price</div><div>Qty</div><div>Total</div>
        </div>
      `;

      const rows = items.map((it, idx) => {
        const price = Number(it.variant.price || 0);
        const total = price * (Number(it.qty) || 0);
        const img = it.product.image ? `<img src="${it.product.image}" alt="">` : "";
        const title = it.product.title || "";
        return `
          <div class="city-fence-calc__results-row" data-result-index="${idx}">
            <div>${img}</div>
            <div>
              <div class="text--strong">${title}</div>
              <div class="text--subdued">${state.height}</div>
            </div>
            <div>${formatMoney(price)}</div>
            <div>
              <div class="city-fence-calc__qty">
                <button type="button" data-qty-dec aria-label="Decrease quantity">−</button>
                <input type="number" min="0" step="1" value="${it.qty}" data-qty-input>
                <button type="button" data-qty-inc aria-label="Increase quantity">+</button>
              </div>
            </div>
            <div class="text--strong" data-row-total>${formatMoney(total)}</div>
          </div>
        `;
      }).join("");

      resultsEl.innerHTML = head + rows;
      updateSummary();
    }

    function getItemsSubtotalCents() {
      if (!state.results) return 0;
      return state.results.items.reduce((sum, it) => {
        const price = Number(it.variant.price || 0);
        const qty = Number(it.qty) || 0;
        return sum + price * qty;
      }, 0);
    }

    function updateSummary() {
      if (!summaryEl || !state.results) return;
      const subtotal = getItemsSubtotalCents();
      summaryEl.innerHTML = `
        <div class="city-fence-calc__summary-inner">
          <div class="city-fence-calc__summary-row">
            <span class="text--subdued">Subtotal</span>
            <span class="text--strong">${formatMoney(subtotal)}</span>
          </div>
          <div class="city-fence-calc__summary-row city-fence-calc__summary-row--total">
            <span>Total</span>
            <span>${formatMoney(subtotal)}</span>
          </div>
        </div>
      `;
    }

    function selectChoice(group, id) {
      if (group === "panels") state.panelProductId = id;
      if (group === "posts") state.postProductId = id;
      if (group === "gravel") state.gravelProductId = id === "none" ? null : id;

      // Rerender selection classes
      if (group === "panels") renderChoices("panels", panelsChoices, filterByHeight(panels), state.panelProductId);
      if (group === "posts") renderChoices("posts", postsChoices, filterByHeight(posts), state.postProductId);
      if (group === "gravel") renderChoices("gravel", gravelChoices, filterByHeight(gravelboards), state.gravelProductId);
    }

    function handleChoiceClick(e) {
      const card = e.target.closest(".city-fence-calc__choice");
      if (!card) return;
      selectChoice(card.dataset.choiceGroup, card.dataset.choiceId);
    }

    panelsChoices.addEventListener("click", handleChoiceClick);
    postsChoices.addEventListener("click", handleChoiceClick);
    gravelChoices.addEventListener("click", handleChoiceClick);

    // Sync feet/meters fields
    let suppress = false;
    elLengthFt.addEventListener("input", () => {
      if (suppress) return;
      suppress = true;
      const ft = Number(elLengthFt.value || 0);
      elLengthM.value = ft ? feetToMeters(ft).toFixed(2) : "";
      suppress = false;
    });
    elLengthM.addEventListener("input", () => {
      if (suppress) return;
      suppress = true;
      const m = Number(elLengthM.value || 0);
      elLengthFt.value = m ? metersToFeet(m).toFixed(2) : "";
      suppress = false;
    });

    elHeight.addEventListener("change", () => {
      state.height = elHeight.value;
    });

    root.addEventListener("click", (e) => {
      const next = e.target.closest("[data-next]");
      const back = e.target.closest("[data-back]");
      const calc = e.target.closest("[data-calculate]");
      const addAll = e.target.closest("[data-add-all]");

      if (next) {
        if (state.step === 1) {
          state.lengthFt = Number(elLengthFt.value || 0);
          state.height = elHeight.value;
          if (!validateStep1()) return;
          // render step 2 options filtered by height
          const panelOpts = filterByHeight(panels, "exact");
          renderChoices("panels", panelsChoices, panelOpts, state.panelProductId || (panelOpts[0] && panelOpts[0].id));
          if (!state.panelProductId && panelOpts[0]) state.panelProductId = panelOpts[0].id;
          setStep(2);
          return;
        }
        if (state.step === 2) {
          const postOpts = filterByHeight(posts, "atOrAbove");
          renderChoices("posts", postsChoices, postOpts, state.postProductId || (postOpts[0] && postOpts[0].id));
          if (!state.postProductId && postOpts[0]) state.postProductId = postOpts[0].id;
          setStep(3);
          return;
        }
        if (state.step === 3) {
          const gravelOpts = filterByHeight(gravelboards, "exact");
          renderChoices("gravel", gravelChoices, gravelOpts, state.gravelProductId);
          setStep(4);
          return;
        }
      }

      if (back) {
        if (state.step === 2) return setStep(1);
        if (state.step === 3) return setStep(2);
        if (state.step === 4) return setStep(3);
        if (state.step === 5) return setStep(4);
      }

      if (calc) {
        recalcResults();
        renderResults();
        setStep(5);
      }

      if (addAll) {
        if (!state.results) return;
        const items = state.results.items
          .filter((it) => (Number(it.qty) || 0) > 0)
          .map((it) => ({ id: it.variant.id, quantity: Number(it.qty) || 0 }));
        if (!items.length) return;

        const totalQty = items.reduce((sum, it) => sum + (Number(it.quantity) || 0), 0);

        document.dispatchEvent(new CustomEvent("theme:loading:start"));
        fetch(`${window.routes.cartAddUrl}.js`, {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
          body: JSON.stringify({ items })
        }).then(async (resp) => {
          document.dispatchEvent(new CustomEvent("theme:loading:end"));
          if (!resp.ok) {
            try {
              const err = await resp.json();
              alert(err && (err.description || err.message) ? (err.description || err.message) : "Could not add items to basket.");
            } catch (e) {
              alert("Could not add items to basket.");
            }
            return;
          }

          // Dispatch on documentElement so the theme Cart component catches it and opens the drawer.
          document.documentElement.dispatchEvent(new CustomEvent("product:added", { bubbles: true, detail: { quantity: totalQty, productAdded: true } }));
        });
      }
    });

    // Qty controls in results (event delegation)
    resultsEl.addEventListener("click", (e) => {
      const row = e.target.closest("[data-result-index]");
      if (!row || !state.results) return;
      const idx = Number(row.dataset.resultIndex);
      const item = state.results.items[idx];
      if (!item) return;
      const input = row.querySelector("[data-qty-input]");
      if (!input) return;

      if (e.target.closest("[data-qty-dec]")) {
        item.qty = clampInt((Number(item.qty) || 0) - 1, 0, 999);
        input.value = String(item.qty);
      }
      if (e.target.closest("[data-qty-inc]")) {
        item.qty = clampInt((Number(item.qty) || 0) + 1, 0, 999);
        input.value = String(item.qty);
      }

      const price = Number(item.variant.price || 0);
      const total = price * (Number(item.qty) || 0);
      const totalEl = row.querySelector("[data-row-total]");
      if (totalEl) totalEl.textContent = formatMoney(total);
      updateSummary();
    });

    resultsEl.addEventListener("change", (e) => {
      const row = e.target.closest("[data-result-index]");
      if (!row || !state.results) return;
      const idx = Number(row.dataset.resultIndex);
      const item = state.results.items[idx];
      if (!item) return;
      const input = e.target.closest("[data-qty-input]");
      if (!input) return;
      item.qty = clampInt(input.value, 0, 999);
      input.value = String(item.qty);
      const price = Number(item.variant.price || 0);
      const total = price * (Number(item.qty) || 0);
      const totalEl = row.querySelector("[data-row-total]");
      if (totalEl) totalEl.textContent = formatMoney(total);
      updateSummary();
    });
  }

  document.addEventListener("DOMContentLoaded", function () {
    document.querySelectorAll("[data-calc-root]").forEach(initCalculator);
  });
})();

